import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  ChevronRight, 
  ChevronDown,
  Search, 
  X, 
  ClipboardList,
  Stethoscope,
  Activity,
  AlertTriangle,
  List
} from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import LanguageToggle from '../components/LanguageToggle';
import { afterVisitInstructions, afterVisitContent } from '../data/afterVisitContent';
import afterVisitTranslations from '../data/afterVisitTranslations.json';

// Category definitions matching user's master list
const categories = [
  {
    id: 'post-procedure',
    name: 'Post-Procedure Recovery',
    nameEs: 'Recuperación Post-Procedimiento',
    icon: Stethoscope,
    description: 'Recovery instructions after urological surgeries and procedures',
    descriptionEs: 'Instrucciones de recuperación después de cirugías y procedimientos urológicos',
    guideIds: [
      'post-ureteroscopy',
      'post-eswl',
      'post-pcnl',
      'cystoscopy',
      'prostate-biopsy',
      'post-turbt',
      'post-bcg',
      'post-prostate-outlet-surgery',
      'post-prostatectomy',
      'post-cystectomy',
      'post-orchiectomy',
      'post-partial-nephrectomy',
      'post-kidney-ablation',
      'vasectomy',
      'post-hydrocelectomy',
      'post-spermatocelectomy',
      'post-varicocelectomy',
      'post-circumcision'
    ]
  },
  {
    id: 'device-care',
    name: 'Device Care',
    nameEs: 'Cuidado de Dispositivos',
    icon: Activity,
    description: 'Managing catheters, stents, and drainage tubes',
    descriptionEs: 'Manejo de catéteres, stents y tubos de drenaje',
    guideIds: [
      'ureteral-stent',
      'urinary-retention-catheter',
      'catheter-removal',
      'suprapubic-catheter',
      'nephrostomy-tube',
      'self-catheterization'
    ]
  },
  {
    id: 'condition-followup',
    name: 'Condition-Related Follow-Up',
    nameEs: 'Seguimiento Relacionado con Condiciones',
    icon: ClipboardList,
    description: 'Managing chronic conditions and follow-up care',
    descriptionEs: 'Manejo de condiciones crónicas y cuidado de seguimiento',
    guideIds: [
      'kidney-stone-conservative',
      'kidney-stone-ed-followup',
      'elevated-psa',
      'bph',
      'recurrent-uti',
      'erectile-dysfunction',
      'active-surveillance-prostate'
    ]
  },
  {
    id: 'symptom-monitoring',
    name: 'Symptom Monitoring',
    nameEs: 'Monitoreo de Síntomas',
    icon: AlertTriangle,
    description: 'When to seek care and recognizing warning signs',
    descriptionEs: 'Cuándo buscar atención y reconocer señales de advertencia',
    guideIds: [
      'hematuria',
      'testicular-pain',
      'warning-signs-guide'
    ]
  }
];

const AfterVisitPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showAllExpanded, setShowAllExpanded] = useState(false);
  const [language, setLanguage] = useState('en');

  // Get translated title for a guide
  const getGuideTitle = (item) => {
    if (language === 'es' && afterVisitTranslations[item.id]) {
      return afterVisitTranslations[item.id].title;
    }
    return item.title;
  };

  // Build searchable content
  const searchableItems = useMemo(() => {
    return afterVisitInstructions.map(item => {
      const content = afterVisitContent[item.id];
      let searchText = `${item.title} ${item.description}`.toLowerCase();
      
      if (content) {
        content.sections?.forEach(section => {
          searchText += ` ${section.heading} ${section.content || ''}`.toLowerCase();
          if (section.items) {
            searchText += ` ${section.items.join(' ')}`.toLowerCase();
          }
        });
      }
      
      return {
        ...item,
        searchText
      };
    });
  }, []);

  // Filter items based on search
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return searchableItems;
    const query = searchQuery.toLowerCase();
    return searchableItems.filter(item => item.searchText.includes(query));
  }, [searchableItems, searchQuery]);

  // Get guides for a category (filtered by search if active)
  const getGuidesForCategory = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return [];
    
    return filteredItems.filter(item => category.guideIds.includes(item.id));
  };

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Expand all categories
  const expandAll = () => {
    const allCategoryIds = categories
      .filter(cat => getGuidesForCategory(cat.id).length > 0)
      .map(cat => cat.id);
    setExpandedCategories(new Set(allCategoryIds));
    setShowAllExpanded(true);
  };

  // Collapse all categories
  const collapseAll = () => {
    setExpandedCategories(new Set());
    setShowAllExpanded(false);
  };

  // Sort all items alphabetically for "Show All"
  const sortedItems = [...filteredItems].sort((a, b) => getGuideTitle(a).localeCompare(getGuideTitle(b)));

  // Check if we're in search mode
  const isSearching = searchQuery.trim().length > 0;

  return (
    <PageWrapper>
      {/* Header */}
      <div className="after-visit-header" data-testid="after-visit-header">
        <div className="after-visit-title">
          <ClipboardList size={28} />
          <div>
            <h1>{language === 'en' ? 'After Visit Instructions' : 'Instrucciones Después de la Visita'}</h1>
            <p>{language === 'en' ? 'Post-procedure care guides and recovery information' : 'Guías de cuidado post-procedimiento e información de recuperación'}</p>
          </div>
        </div>
        <LanguageToggle language={language} onToggle={() => setLanguage(language === 'en' ? 'es' : 'en')} />
      </div>

      {/* Search Bar */}
      <div className="after-visit-search-section">
        <div className="after-visit-search-wrapper">
          <Search size={20} />
          <input
            type="text"
            placeholder={language === 'en' ? 'Search care guides...' : 'Buscar guías de cuidado...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="after-visit-search-input"
            data-testid="after-visit-search-input"
          />
          {searchQuery && (
            <button 
              className="after-visit-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Results Count & Controls */}
      <div className="after-visit-toolbar" data-testid="after-visit-toolbar">
        <div className="after-visit-results-count">
          {filteredItems.length} guide{filteredItems.length !== 1 ? 's' : ''}
          {searchQuery && ` for "${searchQuery}"`}
        </div>
        {!isSearching && (
          <div className="accordion-controls">
            <button onClick={expandAll} className="accordion-control-btn">
              Expand All
            </button>
            <button onClick={collapseAll} className="accordion-control-btn">
              Collapse All
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {isSearching ? (
        /* Search Results - Flat List */
        <div className="after-visit-list" data-testid="instructions-list">
          {filteredItems.length > 0 ? (
            filteredItems.map(item => (
              <Link 
                to={`/after-visit/${item.id}`} 
                key={item.id} 
                className="after-visit-card"
                data-testid={`instruction-card-${item.id}`}
              >
                <FileText size={24} className="after-visit-card-icon" />
                <div className="after-visit-card-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
                <ChevronRight size={20} className="after-visit-card-arrow" />
              </Link>
            ))
          ) : (
            <div className="empty-state" data-testid="empty-state">
              <FileText size={48} />
              <h3>No guides found</h3>
              <p>Try a different search term</p>
              <button 
                className="btn-primary" 
                onClick={() => setSearchQuery('')}
              >
                View All Guides
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Categorized Accordion View */
        <div className="conditions-accordion after-visit-accordion" data-testid="after-visit-accordion">
          {/* Show All Accordion */}
          <div className="accordion-section accordion-show-all" data-testid="accordion-show-all">
            <button
              className={`accordion-header ${showAllExpanded ? 'expanded' : ''}`}
              onClick={() => setShowAllExpanded(!showAllExpanded)}
              data-testid="accordion-header-show-all"
            >
              <div className="accordion-header-left">
                <List size={22} className="accordion-icon" />
                <div className="accordion-title-group">
                  <h3>{language === 'en' ? 'Show All Guides' : 'Mostrar Todas las Guías'}</h3>
                  <span className="accordion-description">{language === 'en' ? 'Browse all care guides alphabetically' : 'Explorar todas las guías alfabéticamente'}</span>
                </div>
              </div>
              <div className="accordion-header-right">
                <span className="accordion-count">{sortedItems.length}</span>
                {showAllExpanded ? (
                  <ChevronDown size={20} className="accordion-chevron" />
                ) : (
                  <ChevronRight size={20} className="accordion-chevron" />
                )}
              </div>
            </button>
            
            {showAllExpanded && (
              <div className="accordion-content accordion-content-all" data-testid="accordion-content-show-all">
                {sortedItems.map(item => (
                  <Link 
                    to={`/after-visit/${item.id}`}
                    key={item.id}
                    className="accordion-article-link"
                    data-testid={`guide-all-${item.id}`}
                  >
                    <span className="article-title">{getGuideTitle(item)}</span>
                    <ChevronRight size={16} className="article-arrow" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Category Accordions */}
          {categories.map(cat => {
            const IconComponent = cat.icon;
            const categoryGuides = getGuidesForCategory(cat.id);
            const count = categoryGuides.length;
            const isExpanded = expandedCategories.has(cat.id);
            
            if (count === 0) return null;
            
            return (
              <div key={cat.id} className="accordion-section" data-testid={`accordion-${cat.id}`}>
                <button
                  className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleCategory(cat.id)}
                  data-testid={`accordion-header-${cat.id}`}
                >
                  <div className="accordion-header-left">
                    <IconComponent size={22} className="accordion-icon" />
                    <div className="accordion-title-group">
                      <h3>{language === 'es' && cat.nameEs ? cat.nameEs : cat.name}</h3>
                      <span className="accordion-description">{language === 'es' && cat.descriptionEs ? cat.descriptionEs : cat.description}</span>
                    </div>
                  </div>
                  <div className="accordion-header-right">
                    <span className="accordion-count">{count}</span>
                    {isExpanded ? (
                      <ChevronDown size={20} className="accordion-chevron" />
                    ) : (
                      <ChevronRight size={20} className="accordion-chevron" />
                    )}
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="accordion-content" data-testid={`accordion-content-${cat.id}`}>
                    {categoryGuides.map(item => (
                      <Link 
                        to={`/after-visit/${item.id}`}
                        key={item.id}
                        className="accordion-article-link"
                        data-testid={`guide-${item.id}`}
                      >
                        <span className="article-title">{getGuideTitle(item)}</span>
                        <ChevronRight size={16} className="article-arrow" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Disclaimer */}
      <div className="after-visit-disclaimer">
        <p>{language === 'en' 
          ? "These are general guidelines. Always follow your doctor's specific instructions." 
          : "Estas son pautas generales. Siempre siga las instrucciones específicas de su médico."}</p>
      </div>
    </PageWrapper>
  );
};

export default AfterVisitPage;
